import { useI18n } from '../../../../../../../application/language/useI18n';
import { inject } from '../../../../../../../common/container/inject';
import { ILearningRootService, LearningRootServiceToken } from '../../../../../../../service/LearningRootService';
import { ContentImportUI } from './ContentImportUI';
import { IContentImportDomain } from './IContentImportDomain';
import { IFileForImport } from './IContentImportUI';

const IMPORT_BATCH_MAX_BYTES = 45 * 1024 * 1024;

type IImportResultLike = {
  success: boolean;
  message?: string;
};

const isImportResultLike = (value: unknown): value is IImportResultLike => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.success === 'boolean';
};

export class ContentImportDomain implements IContentImportDomain {
  private i18n = useI18n();

  constructor(
    public ui = new ContentImportUI(),
    private learningRootService = inject<ILearningRootService>(LearningRootServiceToken),
  ) {}

  handleFilesDrop = (files: FileList | File[]) => {
    this.ui.dropzoneErrors.setList([]);
    const current = this.ui.unitsForImport.list;
    const toAdd: IFileForImport[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const exists = current.some((f) => f.file.name === file.name && f.file.size === file.size);
      if (exists) {
        errors.push(
          this.i18n.translate('contentTransfer.importPage.fileExist', { name: file.name }),
        );
      } else if (!file.name.toLowerCase().endsWith('.zip')) {
        errors.push(
          this.i18n.translate('contentTransfer.importPage.fileNotZip', { name: file.name }),
        );
      } else {
        toAdd.push({ file, status: 'pending' });
      }
    });

    if (toAdd.length > 0) {
      this.ui.unitsForImport.setList([...current, ...toAdd]);
    }
    if (errors.length > 0) {
      this.ui.dropzoneErrors.setList(errors);
      setTimeout(() => {
        this.ui.dropzoneErrors.setList([]);
      }, 5000);
    }
  };

  removeFile = (fileName: string) => {
    const updated = this.ui.unitsForImport.list.filter((f) => f.file.name !== fileName);
    this.ui.unitsForImport.setList(updated);
  };

  clearAllFiles = () => {
    this.ui.unitsForImport.setList([]);
  };

  openPasswordDialog = () => {
    this.ui.importPassword.setEntity('');
    this.ui.passwordError.setEntity(null);
    this.ui.isPasswordDialogOpen.setEntity(true);
  };

  closePasswordDialog = () => {
    this.ui.isPasswordDialogOpen.setEntity(false);
  };

  checkPasswordAndImport = async () => {
    this.closePasswordDialog();
    await this.importAllFiles();
  };

  importAllFiles = async () => {
    const files = [...this.ui.unitsForImport.list];
    if (files.length === 0) return;

    this.ui.isImporting.setEntity(true);
    this.ui.importProgress.setEntity({ current: 0, total: files.length });

    try {
      const results = await this.learningRootService.contentTransfer.importBatchChunked(
        files.map((f) => f.file),
        this.ui.importPassword.entity,
        this.ui.withDependencies.entity,
        {
          maxBatchBytes: IMPORT_BATCH_MAX_BYTES,
          onProgress: (progress) => {
            this.ui.importProgress.setEntity(progress);
          },
        },
      );

      this.ui.unitsForImport.setList(
        files.map((file, idx) => {
          const result = results[idx];
          return {
            ...file,
            status: result?.success ? 'success' : 'error',
            message: result?.success
              ? this.i18n.translate('contentTransfer.importPage.unitState')
              : result?.message || this.i18n.translate('contentTransfer.importPage.importErrorDefault'),
          };
        }),
      );
    } catch (err) {
      const errorObject = err && typeof err === 'object' ? (err as Record<string, unknown>) : {};
      let errorMessage = this.i18n.translate('contentTransfer.importPage.importErrorDefault');

      const messageKey = typeof errorObject.messageKey === 'string' ? errorObject.messageKey : null;
      const errorName = typeof errorObject.errorName === 'string' ? errorObject.errorName : null;
      const rawMessage = typeof errorObject.message === 'string' ? errorObject.message : null;

      if (messageKey) {
        errorMessage = this.i18n.translate(messageKey, { message: rawMessage ?? '' });
      } else if (errorName) {
        errorMessage = this.i18n.translate(`WEB_MESSAGE.${errorName}` as any);
      } else if (rawMessage) {
        errorMessage = rawMessage;
      }

      const processedCountRaw = errorObject.processedCount;
      const chunkSizeRaw = errorObject.chunkSize;
      const partialResultsRaw = errorObject.partialResults;

      const processedCount =
        typeof processedCountRaw === 'number' && Number.isFinite(processedCountRaw) ? processedCountRaw : 0;
      const chunkSize = typeof chunkSizeRaw === 'number' && Number.isFinite(chunkSizeRaw) ? chunkSizeRaw : files.length;
      const partialResults = Array.isArray(partialResultsRaw)
        ? partialResultsRaw.filter(isImportResultLike)
        : [];
      const failedIndexes = new Set<number>();

      for (let index = processedCount; index < Math.min(processedCount + chunkSize, files.length); index += 1) {
        failedIndexes.add(index);
      }

      this.ui.unitsForImport.setList(
        files.map((file, idx) => {
          const partialResult = partialResults[idx];
          if (partialResult) {
            return {
              ...file,
              status: partialResult.success ? 'success' : 'error',
              message: partialResult.success
                ? this.i18n.translate('contentTransfer.importPage.unitState')
                : partialResult.message || this.i18n.translate('contentTransfer.importPage.importErrorDefault'),
            };
          }

          if (failedIndexes.has(idx)) {
            return {
              ...file,
              status: 'error',
              message: errorMessage,
            };
          }

          if (idx >= processedCount + chunkSize) {
            return {
              ...file,
              status: 'pending',
              message: undefined,
            };
          }

          return file;
        }),
      );
    } finally {
      this.ui.importProgress.setEntity({
        current: Math.min(this.ui.importProgress.entity.current, files.length),
        total: files.length,
      });
      this.ui.isImporting.setEntity(false);
    }
  };
  togglePasswordVisibility = () => {
    this.ui.isPasswordVisible.setEntity(!this.ui.isPasswordVisible.entity);
  };
}
